import React from "react";
import { DispatchProp } from "react-redux";
import { BaseControl, ControlProps } from "./BaseControl";
import { QuestionModel } from "../../models/QuestionModel";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
} from "../../models/utilities/settingUtilities";
import { isNullOrEmpty } from "../../utils/index";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { getRendererProps } from "../renderers/BaseRenderer";
import { translate } from "../../models/utilities/translationUtilities";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import StarRatingRenderer from "../renderers/v2/StarRatingRenderer";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";
import { VariableType } from "../../models/enums/VariableType";

interface Props extends ControlProps, DispatchProp {}

interface State {
    preview: number;
    down: boolean;
    value: number;
    starWidth?: number;
    starHeight?: number;
}

export class StarRatingControl extends BaseControl<Props, State> {
    starRating: HTMLDivElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            preview: -1,
            down: false,
            value: -1,
        };
    }

    initializeQuestion(question: QuestionModel, props: Props): void {
        super.initializeQuestion(question, props);
        this.setState({ value: this.getValue(props) });
        const image = new Image();
        image.onload = () => {
            this.setState({
                starWidth: image.width,
                starHeight: image.height,
            });
        };
        const imageUrl = translate(question, "ImageUrl", this.props.language);
        if (!isNullOrEmpty(imageUrl)) {
            image.src = imageUrl;
        }
    }

    didUpdateMentions(question: QuestionModel, prevProps: Props) {
        super.initializeQuestion(question, this.props);
        this.setState({ value: this.getValue(this.props) });
    }

    maxValue() {
        return super.maxValue() || 5;
    }

    split = (question: QuestionModel) => getSettingNumber(question, "SplitStarIn", 1);

    getValue(props: Props) {
        const value = this.getMention(props) ? this.getMentionValue(props) : "0";
        return parseFloat(value);
    }

    setValue = (value: number, reset = false) => {
        const [variable] = this.props.question.variables;
        const action =
            value && !reset
                ? (mentions) => mentions.set(String(value))
                : (mentions) => mentions.clear();
        this.setMentions(this.props, variable.name, action);
    };

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { question, questionnaire, showSoftPrompt, useClientValidation } = this.props;
        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }
        const isRequired =
            question.parent &&
            this.isAnswerRequired(questionnaire, question.parent) &&
            getSettingBoolean(question, "AnswerRequired");
        const hasMentions = this.hasMentions(this.props, question.name);
        if (!showSoftPrompt && isRequired && !hasMentions) {
            const customError =
                getSettingString(question, "CustomErrorMessage") ||
                this.parentCustomError(question.parent);

            return new ValidationBuilder()
                .fail(question.name, customError || "IncompleteAnswer")
                .build();
        }
        return new ValidationBuilder().build();
    }

    renderQuestion(question: QuestionModel) {
        const { sequencedVariables, isDisabled } = this.props;
        const variable = sequencedVariables.find((v) => v.type === VariableType.Continuous);
        return (
            <StarRatingRenderer
                {...getRendererProps(this.props)}
                containerRef={(e) => {
                    this.starRating = e as HTMLDivElement;
                }}
                setValue={this.setValue}
                starWidth={this.state.starWidth}
                starHeight={this.state.starHeight}
                value={this.state.value}
                maxValue={this.maxValue()}
                split={this.split(question)}
                elementId={variable?.elementId}
                isDisabled={isDisabled === true}
            />
        );
    }
}
